/**
 * Working role — whether an attempt's working is part of what is assessed
 * (`marked`) or only the student's means to the answer (`scratch`).
 *
 * It decides what a finding on a RIGHT answer is worth: under `scratch`, only
 * an error the answer survived or a cost in time is worth telling the student;
 * how the working reads is not. Wrong answers are diagnosed the same either way.
 *
 * Decided, first layer that speaks:
 *   1. the sitting's own config (`QSetSession.config.working`) — the paper's,
 *      or the assignment's override of it, chosen when it was set;
 *   2. the tenant setting `working`, then the platform's;
 *   3. `WORKING_DEFAULT` below.
 * Layers 2–3 are keyed by the attempt's modality and the question's class.
 *
 * See docs/FeatureDesigns/diagnosis/accuracy/working-role-target.md.
 */

import type { QuestionSetModality } from './questionSetConfig';

export type WorkingSetting = 'marked' | 'scratch';
export type WorkingRole = WorkingSetting;

/** `freeform`: FREEFORM and STRUCTURED — the working IS the answer. `other`:
 *  every type whose answer is a value or an option. */
export type WorkingQuestionClass = 'freeform' | 'other';

export interface WorkingByClass {
    freeform: WorkingSetting;
    other: WorkingSetting;
}

/** The tenant setting's shape; the code default has the same one. */
export interface WorkingDefaults {
    byModality: {
        practice?: WorkingByClass;
        test?: WorkingByClass;
    };
}

/** In a test only a non-free-form question's answer counts; on an
 *  assignment the working matters. */
export const WORKING_DEFAULT: { byModality: Required<WorkingDefaults['byModality']> } = {
    byModality: {
        test: { freeform: 'marked', other: 'scratch' },
        practice: { freeform: 'marked', other: 'marked' },
    },
};

/** Where the role came from — the tenant layer's own source is passed in. */
export type WorkingRoleSource = 'session' | 'tenant' | 'platform' | 'default';

export interface ResolvedWorkingRole {
    role: WorkingRole;
    source: WorkingRoleSource;
}

export function workingQuestionClass(questionType: string | undefined): WorkingQuestionClass {
    return questionType === 'FREEFORM' || questionType === 'STRUCTURED' ? 'freeform' : 'other';
}

export function resolveWorkingRole(input: {
    /** `QSetSession.config.working`; absent with no session. */
    sessionWorking?: WorkingSetting;
    /** `QSetSession.modality`; absent with no session, which plays as practice. */
    modality?: QuestionSetModality;
    questionType?: string;
    /** The resolved tenant setting, and the layer it came from. Absent ⇒ the
     *  code default. */
    tenant?: { value: WorkingDefaults; source: 'tenant' | 'platform' | 'default' };
}): ResolvedWorkingRole {
    if (input.sessionWorking === 'marked' || input.sessionWorking === 'scratch') {
        return { role: input.sessionWorking, source: 'session' };
    }
    const modality: QuestionSetModality = input.modality ?? 'practice';
    const cls = workingQuestionClass(input.questionType);
    // A tenant value that leaves this modality out falls to the code default
    // for it, not to "marked" — the default is the documented behaviour.
    const fromTenant = input.tenant?.value.byModality[modality]?.[cls];
    if (fromTenant === 'marked' || fromTenant === 'scratch') {
        return { role: fromTenant, source: input.tenant!.source };
    }
    return { role: WORKING_DEFAULT.byModality[modality][cls], source: 'default' };
}
